import React from 'react';
import { useTranslation } from 'react-i18next';
import useAuth from '../auth/feature/auth';
import { useNavigate } from 'react-router-dom';
import { ROUTER_LINKS } from '../common/consts/app-keys.const';

interface ChangeSection {
  type: string;
  items: string[];
}

interface ChangeVersion {
  version: string;
  date: string;
  sections: ChangeSection[];
}

const SECTION_COLORS: Record<string, { bg: string; text: string }> = {
  Added:          { bg: 'var(--badge-green-bg)',  text: 'var(--badge-green-text)'   },
  Fixed:          { bg: 'var(--badge-yellow-bg)', text: 'var(--badge-yellow-text)'  },
  Changed:        { bg: 'var(--badge-blue-bg)',   text: 'var(--badge-blue-text)'    },
  Removed:        { bg: 'var(--badge-red-bg)',    text: 'var(--badge-red-text)'     },
  Deprecated:     { bg: 'var(--badge-purple-bg)', text: 'var(--badge-purple-text)'  },
  Security:       { bg: 'var(--badge-rose-bg)',   text: 'var(--badge-rose-text)'    },
  Infrastructure: { bg: 'var(--badge-sky-bg)',    text: 'var(--badge-sky-text)'     },
  Changes:        { bg: 'var(--color-bg-secondary)', text: 'var(--color-slate-500)' }
};

const SECTION_TYPE_KEYS: Record<string, string> = {
  Added:          'changelog.sectionAdded',
  Fixed:          'changelog.sectionFixed',
  Changed:        'changelog.sectionChanged',
  Removed:        'changelog.sectionRemoved',
  Deprecated:     'changelog.sectionDeprecated',
  Security:       'changelog.sectionSecurity',
  Changes:        'changelog.sectionChanges',
  Infrastructure: 'changelog.sectionInfrastructure'
};

function sectionStyle(type: string) {
  return SECTION_COLORS[type] ?? { bg: 'var(--color-bg-secondary)', text: 'var(--color-slate-500)' };
}

function parseChangelog(md: string): ChangeVersion[] {
  const versions: ChangeVersion[] = [];
  const versionBlocks = md.split(/^## /m).slice(1);

  for (const block of versionBlocks) {
    const lines = block.split('\n');
    const match = lines[0].trim().match(/^(\d+\.\d+\.\d+)\s*(?:-\s*(.+))?/);
    if (!match) continue;

    const version = match[1];
    const date = match[2]?.trim() ?? '';
    const sections: ChangeSection[] = [];
    const body = lines.slice(1).join('\n');

    for (const sBlock of body.split(/^### /m).slice(1)) {
      const sLines = sBlock.split('\n');
      const type = sLines[0].trim();
      const items = sLines
        .slice(1)
        .filter((l) => l.trim().startsWith('- '))
        .map((l) => l.trim().slice(2).trim());
      if (items.length > 0) sections.push({ type, items });
    }

    versions.push({ version, date, sections });
  }

  return versions;
}

const StoreChangelog: React.FC = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [versions, setVersions] = React.useState<ChangeVersion[] | null>(null);

  React.useEffect(() => {
    if (!isAdmin) navigate(ROUTER_LINKS.ROOT);
  }, [isAdmin, navigate]);

  React.useEffect(() => {
    fetch('/store-changelog.md')
      .then((r) => r.text())
      .then((md) => setVersions(parseChangelog(md)))
      .catch(() => setVersions([]));
  }, []);

  if (!isAdmin) return null;

  if (versions === null) {
    return (
      <div className="w-full max-w-[860px] mx-auto py-[40px] px-[20px] text-gray-400">
        {t('changelog.loading')}
      </div>
    );
  }

  return (
    <div className="w-full max-w-[860px] mx-auto py-[40px] px-[20px]">
      <div className="flex items-center gap-[12px] mb-[8px]">
        <button
          onClick={() => navigate(ROUTER_LINKS.ADMIN)}
          className="text-[14px] text-blue hover:underline"
        >
          {t('header.admin')}
        </button>
        <span className="text-gray-400">/</span>
        <h1 className="text-[28px] font-display font-bold text-content-dark tracking-tight">
          {t('changelog.title')}
        </h1>
      </div>
      <p className="text-[14px] text-gray-600 mb-[40px]">
        {t('changelog.subtitle')}
      </p>

      <div className="flex flex-col gap-[32px]">
        {versions.map((v, i) => (
          <div key={i} className="border-l-2 border-gray-200 pl-[24px]">
            <div className="flex items-baseline gap-[12px] mb-[16px]">
              <span className="text-[18px] font-display font-bold text-content-dark">
                v{v.version}
              </span>
              {v.date && (
                <span className="text-[13px] text-gray-400">{v.date}</span>
              )}
            </div>

            <div className="flex flex-col gap-[16px]">
              {v.sections.map((sec, j) => {
                const colors = sectionStyle(sec.type);
                return (
                  <div key={j}>
                    <span
                      className="inline-block rounded-[4px] px-[8px] py-[2px] mb-[10px] text-[11px] font-bold uppercase tracking-wider"
                      style={{ background: colors.bg, color: colors.text }}
                    >
                      {SECTION_TYPE_KEYS[sec.type]
                        ? t(SECTION_TYPE_KEYS[sec.type])
                        : sec.type}
                    </span>
                    <ul className="m-0 pl-[18px] flex flex-col gap-[6px]">
                      {sec.items.map((item, k) => (
                        <li
                          key={k}
                          className="text-[14px] leading-[1.65] text-[var(--color-text-secondary)]"
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StoreChangelog;
